import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import { ActivityList } from './ActivityList';
import type { ActivityListRowProps } from './ActivityListRow';
import { ActivityListRow } from './ActivityListRow';
import type { ActivityRowProps } from './ActivityRow';
import { ActivityRow } from './ActivityRow';
import { AnswerChip, AnswerChipRow } from './AnswerChip';
import { AssistantProse } from './AssistantProse';
import { PillActionRow, PillButton } from './PillButton';
import type { SkeletonBar } from './SkeletonCard';
import { SkeletonCard } from './SkeletonCard';
import { ThumbnailStrip } from './ThumbnailStrip';
import styles from './ActivityBlock.module.css';
import type { ActivityTone, AgentLifecycle, ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** One answer offered under a `question` body. */
export interface ActivityChoice {
  /** The answer itself — `Existing investors`. One line; the row wraps, the chip never does. */
  label: string;
  /** *Asking back* preselects the first of the three. Exactly one chip in a group carries it. */
  selected?: boolean;
  /**
   * Recording the choice.
   *
   * INVENTORY §1.39 writes the choice as `{ label, selected? }` and stops there, which would ship
   * three real `<button>`s wired to nothing. The handler is added rather than the chips downgraded
   * to `div`s, because BUILD-CONTRACT §6 is unconditional about anything clickable and `AnswerChip`
   * is already a button underneath.
   */
  onSelect?: () => void;
}

/**
 * What sits under the row. Six kinds, and a block draws **at most one** of them — no artboard in
 * eighty stacks two bodies under a single header.
 *
 * `none` is not an absence of a body so much as the whole collapsed tier: queue's done row, all
 * four 190px terminal agent states, and every folded step in a conversation column are a bare 20px
 * line with nothing underneath.
 */
export type ActivityBody =
  | { kind: 'none' }
  /** The bordered card of steps — ten of the fourteen `Agent tool` boards, Running, Complete. */
  | { kind: 'list'; rows: ActivityListRowProps[] }
  /**
   * The low-fidelity sketch of the artifact being written — the *Canvas*, *Agent draft* and
   * *Writing artifact* boards.
   */
  | { kind: 'skeleton'; bars?: SkeletonBar[] }
  /** The vision tool's payload — *Image analyze*, and the only body in the file with no rim at all. */
  | { kind: 'thumbnails'; fills: string[] }
  /** The agent stops and asks — *Asking back*. A 14/22 question over a wrapping row of chips. */
  | { kind: 'question'; question: string; choices: ActivityChoice[] }
  /** Plain 14/22 prose — Failed's reason, stated in words rather than an error code. */
  | { kind: 'explanation'; text: string };

/** The pair of exits a resolved or failed state offers. Always primary + ghost, in that order. */
export interface ActivityActions {
  /**
   * `stretched` is Complete's row — a full-width member of the column.
   * `inline` is Failed's — flex and gap only, hugging the two pills at the column's start.
   * The one real geometry difference between the two action rows in the file.
   */
  layout: 'stretched' | 'inline';
  /** The filled pill — `Accept`, `Try again`. */
  primary: string;
  /** The outline pill — `Reject`, `Skip it`. */
  ghost: string;
  onPrimary?: () => void;
  onGhost?: () => void;
}

export interface ActivityBlockProps {
  /**
   * The 20px header line. Passed whole rather than flattened into a dozen props, because
   * `ActivityRow`'s three densities are a discriminated union and re-declaring them here would
   * mean re-deriving which of `meta` / `elapsed` / `disclosure` is legal on which tier.
   *
   * For the eight lifecycle states, build it with `agentLifecycleRow` below rather than by hand.
   */
  row: ActivityRowProps;
  /** Defaults to `{ kind: 'none' }` — the bare line the collapsed tier draws. */
  body?: ActivityBody;
  /** Complete -> Accept/Reject (stretched). Failed -> Try again/Skip it (inline). */
  actions?: ActivityActions;
  theme?: ChatTheme;
  className?: string;
}

/**
 * `AgentLifecycle` -> the mark the row leads with.
 *
 * **`updating` maps to `running`, it does not branch.** The *Updating* artboard exists to prove
 * exactly that: it is byte-identical to *Running* — same spinner at the same 16px in the same
 * `subtle`, same 300px frame, same card with the same third row still spinning — and the only two
 * differences in the whole artboard are the strings `Building outline` -> `Updating outline` and
 * `2 of 3` -> `1 of 3`. Strings are the caller's. So there are seven marks here for eight states,
 * and that is the finding.
 */
const LIFECYCLE_STATE = {
  running: 'running',
  updating: 'running',
  complete: 'complete',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  deleting: 'deleting',
  deleted: 'deleted',
} as const;

/**
 * `AgentLifecycle` -> the tone the icon and the label move to together (never the meta).
 *
 * Read down the column and the ladder from the eight artboards is legible: three states are
 * ordinary work and stay neutral, Accepted is the only green in eighty artboards, Failed the only
 * red, and everything that has left the user's hands — rejected, deleting, deleted — is `muted`,
 * which `ActivityRow` resolves to hint. Rejection is deliberately NOT red: *Rejected*'s caption is
 * *"the work stays as a record"*, and the design separates "you said no" from "it could not".
 */
const LIFECYCLE_TONE: Record<AgentLifecycle, ActivityTone> = {
  running: 'neutral',
  updating: 'neutral',
  complete: 'neutral',
  accepted: 'success',
  rejected: 'muted',
  failed: 'error',
  deleting: 'muted',
  deleted: 'muted',
};

/**
 * Which states wear the chevron.
 *
 * The rule the eight artboards actually encode is *"a chevron only where something folds away"* —
 * so this table is the same shape as "does this state have a body", and the four 190px terminals
 * have neither. It is spelled out per state rather than derived from `body` because the mapper
 * builds a row before a body exists, and because Failed is the one state whose foldable content is
 * a paragraph rather than a card — a body test written from the card alone would drop its chevron.
 *
 * The table is the mapper's best guess at the shape of a turn; `ActivityBlock` gets the last word,
 * because by then the body is in hand. An `up` here over a `kind: 'none'` body is downgraded to
 * `none` there rather than shipped — see the reconciliation at the top of the component.
 */
const LIFECYCLE_DISCLOSURE: Record<AgentLifecycle, 'up' | 'none'> = {
  running: 'up',
  updating: 'up',
  complete: 'up',
  accepted: 'none',
  rejected: 'none',
  failed: 'up',
  deleting: 'none',
  deleted: 'none',
};

export interface AgentLifecycleRowOptions {
  /** Which of the eight states `Agent state` draws. */
  lifecycle: AgentLifecycle;
  /** `Building outline`, `Built the outline`, `Outline accepted`, `Deleted Retention`. */
  label: string;
  /**
   * The right-hand run: a counter while working (`2 of 3`), an outcome noun when resolved
   * (`3 artifacts`, `reverted`), an identifier when destructive (`artifact 02`), and **absent on
   * Failed** — the one chevron-bearing state in the column with nothing to count.
   */
  meta?: string;
  onToggle?: () => void;
  /**
   * **Required here, unlike every other `theme` in the folder.**
   *
   * Everywhere else the prop defaults to `'dark'` and that is harmless, because the component
   * applies the class itself and a composite can still override it at the call site. This one
   * cannot be overridden: it PRE-RENDERS the mark (`<ActivityGlyph … theme={theme}/>`) and writes
   * `theme` into the row object it returns, so a default would bake dark into a row that a light
   * block then cannot correct — `<ActivityBlock theme="light" row={agentLifecycleRow({…})}/>`
   * would paint a dark header line and a dark glyph over the light ground while the body below
   * stayed light. BUILD-CONTRACT §2 is unconditional: light is a token swap across the whole
   * block. Making the caller thread the block's theme through is the only way both halves agree.
   */
  theme: ChatTheme;
}

/**
 * The eight `Agent state` artboards, as one line each.
 *
 * This is where `AgentLifecycle` lives. It is a mapper and not a prop on `ActivityBlock` because
 * the block's header is an `ActivityRow` of any tier — a lifecycle state, a tool card, a folded
 * step, the thinking line — and only one of those four is a lifecycle. Folding the union into the
 * component would make every non-lifecycle caller pass a state that means nothing to them.
 *
 * Everything the lifecycle decides is decided here: the mark, the tone of the mark and the label,
 * and whether there is a chevron. Everything it does not decide — the strings, the counter, the
 * body — stays with the caller, which is the whole reason Running and Updating collapse to one row.
 */
export function agentLifecycleRow({
  lifecycle,
  label,
  meta,
  onToggle,
  theme,
}: AgentLifecycleRowOptions): ActivityRowProps {
  return {
    // Every one of the eight is the open tier: a 16px mark with a 14/20 label and a 13/20 meta.
    // Even the four collapsed 190px terminals — "collapsed" there means the card is gone, not that
    // the line stepped down a tier. `ActivityRow`'s `collapsed` is a different thing entirely (the
    // folded step in a conversation column, 15px and 13/20).
    density: 'open',
    icon: <ActivityGlyph state={LIFECYCLE_STATE[lifecycle]} size={16} theme={theme} />,
    label,
    meta,
    tone: LIFECYCLE_TONE[lifecycle],
    disclosure: LIFECYCLE_DISCLOSURE[lifecycle],
    onToggle,
    theme,
  };
}

/**
 * An activity row and at most one body, in the 8px group.
 *
 * This is what "one tool run", "one agent state" and "one step group" all reduce to — the object
 * four separate build agents each drew a different wrapper around. All fourteen `Agent tool`
 * artboards and all eight `Agent state` artboards are this component: the fourteen differ only in
 * which mark leads the row and which of three bodies hangs off it, and the eight differ only in the
 * mark's tone and whether there is a body at all.
 *
 * The two numbers it owns are 8 and 12, and they carry the information architecture of an agent
 * turn: **a header and its body are one object at 8px apart, and objects are 12px apart** in the
 * conversation column above. Neither is a prop. The 12 is not even in this file — it belongs to the
 * column — which is exactly why the 8 has to live somewhere and this is the somewhere.
 *
 * It also owns the block's `aria-live` region, and that is load-bearing rather than decorative:
 * `SkeletonCard` and `ThumbnailStrip` are both `aria-hidden` by construction (three unlabelled
 * rectangles are noise, and the row above already says "Writing Retention · artifact 02" in words),
 * so without a region here a card that swapped its skeleton for a finished list would change
 * silently. Owning it means switching the children's own regions off — `ActivityRow` and
 * `ActivityList` each declare one when they stand alone, and the block passes `announce={false}`
 * to both. See the note on the region in the body.
 */
export function ActivityBlock({
  row,
  body = { kind: 'none' },
  actions,
  theme: themeProp,
  className,
}: ActivityBlockProps) {
  const theme = useV15Theme(themeProp);
  /*
   * The chevron and the body, reconciled — and this is the only place either can be.
   *
   * `ActivityRow` cannot see what is under it, so it derives `aria-expanded` from its TIER
   * (`density === 'open'`) rather than from whether anything is actually there. The block holds
   * both facts, and agent-state.md is explicit about what they owe each other: "the up-chevron
   * (15×15, always `--color-text-white-hint`) appears only when there is expandable content …
   * absent on all four 190px collapsed states."
   *
   * So an `open` row with nothing beneath it loses both the chevron and the handler. Dropping
   * `onToggle` is the load-bearing half: it is what keeps `ActivityRow` on its `<div>` branch, and
   * therefore what stops a button announcing `aria-expanded="true"` over an empty subtree while
   * pointing an up-chevron at nothing. This is a live case rather than a hypothetical —
   * `agentLifecycleRow` forwards whatever `onToggle` it was handed even on the four terminals,
   * whose `LIFECYCLE_DISCLOSURE` is already `none`.
   *
   * A row the user has FOLDED is a different object and the design draws it as one: the collapsed
   * tier with a down-chevron (the *Founder · cold start* thread's "Read 2 files"). There the row is
   * genuinely a control over content that exists but is hidden, `aria-expanded={false}` is already
   * true, and this leaves it untouched — which is why the guard tests `density === 'open'` and not
   * just the body.
   *
   * `actions` counts as content: a pill row is as foldable as a card, even though no artboard in
   * the file ships one without a body above it.
   */
  const foldable = body.kind !== 'none' || Boolean(actions);
  const headerRow: ActivityRowProps =
    row.density === 'open' && !foldable ? { ...row, disclosure: 'none', onToggle: undefined } : row;

  return (
    <div
      className={cn(chatThemeClass(theme), styles.root, className)}
      /* The region for the whole block, and the reason it sits here rather than on any one child:
         the thing that changes is which child exists. A run swaps `skeleton` for `list`, Complete
         grows a pill row that Running did not have, Failed replaces a card with a sentence — and
         each of those is a mutation of THIS element's subtree, not of anything below it. A region
         scoped to the row's label, or to the card, is blind to every one of those steps.

         **The invariant: exactly one live region per announcing subtree, and a standalone row
         still announces.** So this is the only `aria-live` in an assembled block — `ActivityRow`
         and `ActivityList` are both used bare (their own stories draw them that way, and the specs
         draw loose rows), so each declares a region by default and the block turns it off with
         `announce={false}` rather than either of them deleting theirs. Nothing below is silenced by
         that: a label going "Building outline" -> "Built the outline" is a mutation inside THIS
         region and is still read, it is simply read by the element that can also see the card
         appear underneath it.

         Nesting the three was legal — a mutation is attributed to the nearest ancestor region, so
         nothing announced twice — but it left three elements owning one job, which is a thing that
         only stays correct for as long as everybody remembers all three.

         `aria-atomic` stays at its default `false`: the whole block re-read on every step would be
         unusable on a four-step run, and with it off a step change is announced as that step.

         Nothing is announced when a block first mounts: a polite region's initial content is not
         read, so a page loading a settled thread stays silent. */
      aria-live="polite"
    >
      {/* `theme` AFTER the spread, so the block's theme always wins over whatever the row object
          carries — the folder's precedent is `UserMessage`'s `<UserBubble {...bubble} theme={theme}>`.
          `row.theme ?? theme` looked equivalent and was not: `agentLifecycleRow` always writes a
          `theme`, so the fallback could never fire and a light block rendered a dark header. */}
      {/* `announce={false}` for the same reason as `theme`, and after the spread for the same
          reason too: the block owns the one live region in this subtree, so the row's own label
          region is redundant here. A row rendered anywhere else keeps it. */}
      <ActivityRow {...headerRow} announce={false} theme={theme} />

      {body.kind === 'list' ? (
        /* Announced by the block, not by the card — a `list` body is one of the things that
           appears and disappears under this header, so the region that has to see it is the one
           above. A bare `ActivityList` still announces its own rows. */
        <ActivityList announce={false} theme={theme} className={styles.reveal}>
          {body.rows.map((listRow, i) => (
            <ActivityListRow
              // Steps are positional and their labels legitimately repeat across two cards in one
              // turn, so the index is the only stable identity a row has. The list is rebuilt whole
              // on every stream tick, so there is nothing a better key could buy.
              key={i}
              {...listRow}
              // Same rule as the header row above: the composite's theme is applied after the
              // spread and wins. A row inside a light card is never dark.
              theme={theme}
            />
          ))}
        </ActivityList>
      ) : null}

      {body.kind === 'skeleton' ? (
        <SkeletonCard bars={body.bars} theme={theme} className={styles.reveal} />
      ) : null}

      {body.kind === 'thumbnails' ? (
        <ThumbnailStrip fills={body.fills} theme={theme} className={styles.reveal} />
      ) : null}

      {body.kind === 'question' ? (
        <div className={cn(styles.question, styles.reveal)}>
          {/* Paper wraps the question in a bare self-stretch div and puts the type on the run
              inside it. `AssistantProse` IS that box — 14/22 subtle, full width, never shrinking —
              so the wrapper is dropped rather than reproduced as an empty element. */}
          <AssistantProse theme={theme}>{body.question}</AssistantProse>
          {/* A real group, named by the question. Without it a screen reader meets three loose
              buttons with no idea which sentence they answer — and the answer is two lines up,
              which is exactly the distance at which that stops being obvious.

              `aria-label` rather than `aria-labelledby`: `AssistantProse` takes no `id`, and
              minting one would mean either editing an audited sibling or wrapping its paragraph in
              a second box purely to hold an attribute. The string is the same either way — it is
              read once as prose and once on entering the group, which is what a group label is
              for. */}
          <div role="group" aria-label={body.question} className={styles.choices}>
            <AnswerChipRow theme={theme}>
              {body.choices.map((choice, i) => (
                // Two answers to one question are never the same string, but the array is the
                // caller's and an index keeps a duplicate from silently dropping a chip.
                <AnswerChip
                  key={i}
                  label={choice.label}
                  selected={choice.selected}
                  onSelect={choice.onSelect}
                  theme={theme}
                />
              ))}
            </AnswerChipRow>
          </div>
        </div>
      ) : null}

      {body.kind === 'explanation' ? (
        /* Same box as the question's paragraph and the same metrics, and that is the design's own
           call: Failed's reason is 14/22 `subtle` while the red stays on the row above it. The
           sentence explains, it does not alarm. */
        <AssistantProse theme={theme} className={styles.reveal}>
          {body.text}
        </AssistantProse>
      ) : null}

      {actions ? (
        <PillActionRow layout={actions.layout} theme={theme} className={styles.reveal}>
          <PillButton
            label={actions.primary}
            variant="primary"
            onClick={actions.onPrimary}
            theme={theme}
          />
          <PillButton
            label={actions.ghost}
            variant="ghost"
            onClick={actions.onGhost}
            theme={theme}
          />
        </PillActionRow>
      ) : null}
    </div>
  );
}
